/**
 * select.js
 */

import React from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  ListSubheader,
  OutlinedInput,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash.get';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  select: {
    '& .MuiSelect-select': {
      '& > .MuiSvgIcon-root': {
        display: props => props.hideValueIcon ? 'none' : 'block',
      }
    }
  },
  hideMenuItem: {
    display: 'none',
  },
}));


const nullReplacementValue = 'totally-unique-null-value-1337';


export default function Select(props) {
  const {
    formContext: formContextProp,
    name,
    label,
    disabled,
    className,
    style,
    sx,
    rules,
    options,
    onChange,
    defaultValue,
    displayEmpty,
    setValueAs,
    disableErrorMessage,
    focusHandlers,
    hideValueIcon,
    supportNull,
    dataTestId,
    confirmOpen,
    ...rest
  } = props;

  const classes = useStyles({ hideValueIcon });

  const [open, setOpen] = React.useState(false);
  const formContextDefault = useFormContext();
  const formContext = formContextProp || formContextDefault;
  const error = get(formContext.formState.errors, name);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = async () => {
    if (!confirmOpen || await confirmOpen()) {
      setOpen(true);
    }
  };

  if (!options) return null;

  return (
    <Controller
      control={formContext.control}
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field: { ref, value, onChange, ...inputProps } }) => {  
        return (
          <>
            {/* select control */}
            <FormControl
              variant="outlined"
              size="small"
              error={Boolean(error)}
              className={className}
              style={style}
              sx={sx}
              disabled={disabled}
              data-testid={dataTestId}
              onFocus={e => {
                if (focusHandlers) {
                  focusHandlers.onFocus(e);
                }
              }}
              onBlur={e => {
                if (focusHandlers) {
                  focusHandlers.onBlur(e);
                }
              }}
            >
              {/* label */}
              <InputLabel shrink={displayEmpty || undefined} id={`select-${name}`}>
                {label}
              </InputLabel>

              {/* select input */}
              <MuiSelect
                open={open}
                onClose={handleClose}
                onOpen={handleOpen}
                labelId={`select-${name}`}
                label={label}
                inputRef={ref}
                className={classes.select}
                data-testid={`${name}-select`}
                displayEmpty={displayEmpty}
                input={<OutlinedInput label={label} notched={displayEmpty || undefined} />}

                // try to determine the reasonable value to use based on type of provided value:
                // if undefined/null then use '' or null replacement
                // if object then use id property
                // otherwise use value as-is
                value={value === undefined || value === null
                  ? supportNull
                    ? nullReplacementValue
                    : '' 
                  : typeof value === 'object'
                    ? value.id
                    : value}

                onChange={e => {
                  const value = supportNull && e.target.value === nullReplacementValue
                    ? null
                    : e.target.value;
                  onChange(setValueAs ? setValueAs(value) : value);
                  if (props.onChange) props.onChange(e, value);
                }}
                
                {...inputProps}
                {...rest}
              >
                {options.map(option => {
                  return (
                    option.isHeader
                      ? <ListSubheader key={`header-${option.label}`}>{option.label}</ListSubheader>
                      : <MenuItem
                        key={`select-${name}-${option.id}`}
                        value={supportNull && option.id === null ? nullReplacementValue : option.id}
                        disabled={option.disabled}
                        className={option.hidden ? classes.hideMenuItem : null}
                      >
                        {option.icon &&
                          <Icon type={option.icon} style={{ marginRight: 8 }} />}
                        {option.name}
                      </MenuItem>
                  );
                })}
              </MuiSelect>

              {/* error helper text */}
              {!disableErrorMessage && Boolean(error) &&
              <FormHelperText>
                {error?.message}
              </FormHelperText>}

            </FormControl>
          </>
        );
      }}
    />
  );
}